import { clearLoggedOutLocalBootstrapCredentialForCurrentEnvironment } from "../../environments/primary";
import { reloadDesktopRendererAfterBridgeUpdate } from "../../lib/desktopRendererRebootstrap";
import {
  resolveAuthRedirectReasonMessage,
  takeAuthExpiredReason,
} from "../../lib/authExpiredRedirect";

const INVALID_BOOTSTRAP_CREDENTIAL_MESSAGE = "Invalid bootstrap credential.";

export async function startDesktopCmmdLoginIfAvailable(): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }
  const startCmmdLogin = window.desktopBridge?.desktopAuth?.startCmmdLogin;
  if (!startCmmdLogin) {
    return false;
  }
  await startCmmdLogin();
  clearLoggedOutLocalBootstrapCredentialForCurrentEnvironment();
  reloadDesktopRendererAfterBridgeUpdate();
  return true;
}

export async function startDesktopCmmdPasswordLoginIfAvailable(input: {
  readonly email: string;
  readonly password: string;
}): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }
  const startCmmdPasswordLogin = window.desktopBridge?.desktopAuth?.startCmmdPasswordLogin;
  if (!startCmmdPasswordLogin) {
    return false;
  }
  await startCmmdPasswordLogin(input);
  clearLoggedOutLocalBootstrapCredentialForCurrentEnvironment();
  reloadDesktopRendererAfterBridgeUpdate();
  return true;
}

export function resolveInitialAuthMessage(
  initialErrorMessage: string | undefined,
): string | undefined {
  // One-shot: reads and removes the sessionStorage key so the toast fires only once.
  const reason = takeAuthExpiredReason();
  if (initialErrorMessage) return initialErrorMessage;
  return resolveAuthRedirectReasonMessage(reason);
}

export function errorMessageFromUnknown(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return formatAuthErrorMessage(error.message);
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return formatAuthErrorMessage(error);
  }

  return "Authentication failed.";
}

export function formatAuthErrorMessage(message: string | undefined): string {
  const parsedMessage = parseAuthErrorMessage(message);

  if (parsedMessage === INVALID_BOOTSTRAP_CREDENTIAL_MESSAGE) {
    return [
      "That sign-in link has expired or was already used.",
      "Restart Forge and open the newest pairing link from the server.",
    ].join("\n");
  }

  return parsedMessage;
}

export function parseAuthErrorMessage(message: string | undefined): string {
  const trimmedMessage = message?.trim() ?? "";
  if (!trimmedMessage) {
    return "";
  }

  try {
    const payload = JSON.parse(trimmedMessage) as {
      readonly error?: unknown;
      readonly message?: unknown;
    };
    const payloadMessage = payload.message ?? payload.error;
    if (typeof payloadMessage === "string" && payloadMessage.trim().length > 0) {
      return payloadMessage.trim();
    }
  } catch {
    // The server usually returns JSON, but keep plain-text messages as-is.
  }

  return trimmedMessage;
}

/**
 * Leave `/login` with a document load after the server established the session.
 *
 * An SPA navigation cannot escape here. `__root`'s `beforeLoad` captured
 * `authGateState` before the session cookie existed, and its component renders
 * `<Navigate to="/login">` for any non-authenticated context. So `/login`
 * redirects to `/` on a fresh gate read, the stale root context bounces it back,
 * and the two disagree forever — observed as ~90 `/` ↔ `/login` replacements
 * with the user still looking at the login form while fully authenticated.
 *
 * Forge Cloud's other owner lane never hits this: its OAuth callback finishes
 * with a server 302, which is a document load. This makes the password lane
 * exit the same way, so both Cloud lanes rebuild router context from scratch
 * against the cookie that now exists.
 */
export function reloadIntoAuthenticatedApp(): void {
  window.location.assign("/");
}
